/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import prettyBytes from 'pretty-bytes';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

const FileUploadStatus = observer(({ file }) => {
  if (file.status === 'PENDING') {
    return <span className="upload-status-badge upload-status-pending">Pending</span>;
  }
  if (file.status === 'UPLOADING') {
    const percent = Math.min(Math.floor((file.uploaded / file.size) * 100), 100);
    return (
      <div className="upload-progress-bar">
        <div className="upload-progress-fill" style={{ width: `${percent}%` }} />
        <span className="upload-progress-text">{percent}%</span>
      </div>
    );
  }
  if (file.status === 'FAILED') {
    return (
      <span className="upload-status-badge upload-status-failed">
        <Icon name="times circle" style={iconStyle} /> {file.error || 'Error'}
      </span>
    );
  }
  if (file.status === 'COMPLETE') {
    return (
      <span className="upload-status-badge upload-status-complete">
        <Icon name="check circle" style={iconStyle} /> Complete
      </span>
    );
  }
  return <span className="upload-status-badge">Unknown</span>;
});

const FileUploadToolbar = observer(({ file, state, onClickRemove, onClickCancel }) => {
  if (file.status === 'PENDING') {
    return (
      <button
        className="upload-row-action"
        onClick={onClickRemove}
        disabled={state !== 'PENDING'}
        aria-label="Remove file"
      >
        <Icon name="trash alternate outline" style={iconStyle} />
      </button>
    );
  }
  if (file.status === 'UPLOADING') {
    return (
      <button
        className="upload-row-action upload-row-action-cancel"
        onClick={onClickCancel}
        aria-label="Cancel upload"
      >
        <Icon name="close" style={iconStyle} />
      </button>
    );
  }
  return null;
});

const FileUploadRow = observer(({ file, state, onClickRemove, onClickCancel }) => (
  <tr>
    <td><span className="upload-file-folder">{file.folder}</span></td>
    <td><span className="upload-file-name">{file.name}</span></td>
    <td><span className="upload-file-size">{prettyBytes(file.size)}</span></td>
    <td><FileUploadStatus file={file} /></td>
    <td>
      <FileUploadToolbar file={file} state={state} onClickRemove={onClickRemove} onClickCancel={onClickCancel} />
    </td>
  </tr>
));

const FileUploadTable = observer(({ files = [], state, onClickRemoveFile, onClickCancelFile }) => (
  <div className="upload-table-wrapper">
    <table className="config-table upload-table">
      <thead>
        <tr>
          <th>Folder</th>
          <th>Filename</th>
          <th>Size</th>
          <th>Status</th>
          <th style={{ width: '48px' }} />
        </tr>
      </thead>
      <tbody>
        {files.map(file => (
          <FileUploadRow
            key={file.id}
            file={file}
            state={state}
            onClickRemove={() => onClickRemoveFile(file.id)}
            onClickCancel={() => onClickCancelFile(file.id)}
          />
        ))}
      </tbody>
    </table>
  </div>
));

export default FileUploadTable;
